import { useState, useEffect, FormEvent, ChangeEvent, useRef } from "react";
import { FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useAuth } from "../../context/AuthContext";
import documentacoesService, {
  Documentacao,
  CreateDocumentacaoPayload,
  UpdateDocumentacaoPayload,
} from "../../services/documentacoes";
import blob from "../../services/blob";
import AuthenticatedImage from "../ui/AuthenticatedImage";

interface DocumentacaoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentacao: Documentacao | null;
  onSuccess: () => void;
}

function DocumentacaoEditModal({
  isOpen,
  onClose,
  documentacao,
  onSuccess,
}: DocumentacaoEditModalProps) {
  const { getUser } = useAuth();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [form, setForm] = useState({
    title: "",
    text: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (documentacao) {
        setForm({
          title: documentacao.title || "",
          text: documentacao.text || "",
        });
      } else {
        setForm({
          title: "",
          text: "",
        });
      }
    }
  }, [isOpen, documentacao]);

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    const user = getUser();

    if (!user?.id) {
      return; // Se não houver usuário, deixa o comportamento padrão
    }

    // Procura por imagens nos itens colados
    for (const item of Array.from(items)) {
      if (item.type.includes("image")) {
        e.preventDefault(); // Previne o comportamento padrão
        
        const file = item.getAsFile();
        if (!file) continue;

        setUploadingImage(true);
        toast.info("Fazendo upload da imagem...", { autoClose: 2000 });

        try {
          // Converte a imagem para base64
          const reader = new FileReader();
          reader.onloadend = async () => {
            try {
              const base64String = reader.result as string;
              const base64Data = base64String.split(",")[1];
              
              // Gera um nome de arquivo único
              const timestamp = Date.now();
              const extension = file.name.split(".").pop() || "png";
              const fileName = `doc_${timestamp}.${extension}`;

              // Faz upload usando o serviço que retorna URL pré-assinada
              const uploadResult = await blob.uploadImageWithUrl(
                String(user.id),
                base64Data,
                fileName
              );

              if (uploadResult?.url) {
                // Insere a imagem no markdown no formato ![alt](url)
                const textarea = textareaRef.current;
                if (textarea) {
                  const cursorPosition = textarea.selectionStart;
                  const textBefore = form.text.substring(0, cursorPosition);
                  const textAfter = form.text.substring(cursorPosition);
                  
                  // Insere a imagem no formato markdown
                  const imageMarkdown = `![${fileName}](${uploadResult.url})`;
                  const newText = textBefore + imageMarkdown + textAfter;
                  
                  setForm({ ...form, text: newText });
                  
                  // Reposiciona o cursor após a imagem inserida
                  setTimeout(() => {
                    const newCursorPosition = cursorPosition + imageMarkdown.length;
                    textarea.setSelectionRange(newCursorPosition, newCursorPosition);
                    textarea.focus();
                  }, 0);
                }

                toast.success("Imagem adicionada com sucesso!");
              } else {
                toast.error("Erro ao fazer upload da imagem.");
              }
            } catch (error) {
              console.error("Erro ao processar imagem:", error);
              toast.error("Erro ao processar a imagem.");
            } finally {
              setUploadingImage(false);
            }
          };

          reader.onerror = () => {
            toast.error("Erro ao ler a imagem.");
            setUploadingImage(false);
          };

          reader.readAsDataURL(file);
        } catch (error) {
          console.error("Erro ao fazer upload:", error);
          toast.error("Erro ao fazer upload da imagem.");
          setUploadingImage(false);
        }
        
        break; // Processa apenas a primeira imagem encontrada
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result: Documentacao | null = null;

      if (documentacao?.id) {
        // Atualizar
        const payload: UpdateDocumentacaoPayload = {
          title: form.title,
          text: form.text,
        };
        result = await documentacoesService.update(documentacao.id, payload);
      } else {
        // Criar
        const payload: CreateDocumentacaoPayload = {
          title: form.title,
          text: form.text,
        };
        result = await documentacoesService.create(payload);
      }

      setLoading(false);

      if (result) {
        toast.success(
          documentacao
            ? "Documentação atualizada com sucesso!"
            : "Documentação criada com sucesso!"
        );
        onSuccess();
        onClose();
      } else {
        toast.error(
          documentacao
            ? "Erro ao atualizar documentação, por favor, tente novamente."
            : "Erro ao criar documentação, por favor, tente novamente."
        );
      }
    } catch (error) {
      setLoading(false);
      toast.error("Erro ao salvar documentação.");
    }
  };

  // Componente personalizado para renderizar imagens no markdown
  const MarkdownImage = ({ src, alt }: { src?: string; alt?: string }) => {
    if (!src) return null;

    // Se for uma URL completa, usa diretamente
    if (src.startsWith("http://") || src.startsWith("https://")) {
      return (
        <img
          src={src}
          alt={alt || ""}
          className="w-full rounded-lg my-3 sm:my-4 shadow-md max-h-[120px] sm:max-h-[150px] object-contain"
        />
      );
    }

    // Se for um caminho relativo, tenta usar AuthenticatedImage
    const user = getUser();
    return (
      <div className="my-3 sm:my-4">
        <AuthenticatedImage
          fileName={src}
          alt={alt || ""}
          className="w-full rounded-lg shadow-md"
          professionalId={user?.id ? String(user.id) : "all_view"}
        />
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto transition-all duration-300 p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative w-full max-h-[95dvh] sm:max-h-[90vh] sm:max-w-2xl lg:max-w-7xl bg-white shadow-lg dark:bg-boxdark dark:border dark:border-strokedark rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 overflow-y-auto flex flex-col">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute right-3 top-3 sm:right-4 sm:top-4 p-2.5 -m-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
          aria-label="Fechar modal"
        >
          <FiX size={22} className="sm:w-6 sm:h-6" />
        </button>

        <div className="mb-3 sm:mb-4 pr-10 sm:pr-0">
          <h3 className="text-base sm:text-xl font-bold text-gray-800 dark:text-white">
            {documentacao ? "Editar Documentação" : "Nova Documentação"}
          </h3>
          <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {documentacao
              ? "Edite o título e o conteúdo em Markdown"
              : "Preencha os campos para criar uma nova documentação"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 flex flex-col min-h-0 flex-1">
          {/* Título */}
          <div className="flex-shrink-0">
            <label
              htmlFor="title"
              className="mb-1 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              Título <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={form.title}
              onChange={({ target }: ChangeEvent<HTMLInputElement>) => {
                setForm({ ...form, [target.name]: target.value });
              }}
              placeholder="Digite o título da documentação"
              required
              className="w-full rounded-xl border border-stroke bg-gray-50 py-3 sm:py-2.5 pl-3 sm:pl-4 pr-3 text-base sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation min-h-[48px]"
            />
          </div>

          {/* Editor e Preview: coluna no mobile, linha no lg */}
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 min-h-0 flex-1">
            {/* Editor Markdown */}
            <div className="flex-1 min-h-0 flex flex-col">
              <label
                htmlFor="text"
                className="mb-1 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 flex-shrink-0"
              >
                Conteúdo (Markdown) <span className="text-red-500">*</span>
              </label>
              <textarea
                ref={textareaRef}
                id="text"
                name="text"
                rows={12}
                value={form.text}
                onChange={({ target }: ChangeEvent<HTMLTextAreaElement>) => {
                  setForm({ ...form, [target.name]: target.value });
                }}
                onPaste={handlePaste}
                placeholder="Digite o conteúdo em Markdown... (Ctrl+V para colar imagens)"
                required
                disabled={uploadingImage}
                className="w-full min-h-[220px] max-h-[45vh] sm:min-h-[280px] lg:min-h-[400px] lg:max-h-[520px] lg:h-[520px] rounded-xl border border-stroke bg-gray-50 py-3 pl-3 sm:pl-4 pr-3 text-sm sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation resize-y lg:resize-none font-mono disabled:opacity-50 disabled:cursor-wait"
              />
              <p className="mt-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                Use Markdown para formatar. Suporta imagens, listas, tabelas.
                {uploadingImage && (
                  <span className="ml-2 text-purple-600 dark:text-purple-400">
                    Fazendo upload da imagem...
                  </span>
                )}
              </p>
            </div>

            {/* Preview */}
            <div className="flex-1 min-h-0 flex flex-col">
              <label className="mb-1 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 flex-shrink-0">
                Pré-visualização
              </label>
              <div className="flex-1 min-h-[200px] max-h-[45vh] sm:min-h-[280px] lg:max-h-[520px] lg:h-[520px] overflow-y-auto rounded-xl border border-stroke bg-white dark:border-strokedark dark:bg-boxdark p-3 sm:p-6">
                {form.text.trim() ? (
                  <div className="prose prose-sm sm:prose-base lg:prose-lg dark:prose-invert max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        img: ({ src, alt }) => (
                          <MarkdownImage src={src} alt={alt} />
                        ),
                        h1: ({ children }) => (
                          <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3 mt-4 sm:mb-4 sm:mt-6">
                            {children}
                          </h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 mt-3 sm:mb-3 sm:mt-5">
                            {children}
                          </h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-base sm:text-xl lg:text-2xl font-semibold text-gray-900 dark:text-white mb-2 mt-3 sm:mt-4">
                            {children}
                          </h3>
                        ),
                        p: ({ children }) => (
                          <p className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 mb-3 sm:mb-4 leading-relaxed">
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300">
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300">
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li className="text-sm sm:text-base lg:text-lg">{children}</li>
                        ),
                        code: ({ children, className }) => {
                          const isInline = !className;
                          return isInline ? (
                            <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-purple-600 dark:text-purple-400">
                              {children}
                            </code>
                          ) : (
                            <code className="block bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm font-mono text-gray-800 dark:text-gray-200 mb-4">
                              {children}
                            </code>
                          );
                        },
                        pre: ({ children }) => (
                          <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto mb-4">
                            {children}
                          </pre>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-purple-500 pl-4 italic text-gray-600 dark:text-gray-400 my-4">
                            {children}
                          </blockquote>
                        ),
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 dark:text-purple-400 hover:underline"
                          >
                            {children}
                          </a>
                        ),
                        table: ({ children }) => (
                          <div className="overflow-x-auto my-3 sm:my-4 -mx-1 sm:mx-0">
                            <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-700 text-sm sm:text-base">
                              {children}
                            </table>
                          </div>
                        ),
                        th: ({ children }) => (
                          <th className="border border-gray-300 dark:border-gray-700 px-2 py-1.5 sm:px-4 sm:py-2 bg-gray-100 dark:bg-gray-800 font-semibold text-left text-xs sm:text-base">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="border border-gray-300 dark:border-gray-700 px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-base">
                            {children}
                          </td>
                        ),
                      }}
                    >
                      {form.text}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-400 dark:text-gray-500">
                    <p className="text-sm">A pré-visualização aparecerá aqui...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 justify-end pt-4 border-t border-stroke dark:border-strokedark flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-stroke px-4 sm:px-6 py-3 sm:py-2.5 text-sm sm:text-base font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-strokedark dark:text-white dark:hover:bg-boxdark touch-manipulation active:scale-95 disabled:active:scale-100 min-h-[48px] sm:min-h-0"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !form.title.trim() || !form.text.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 sm:px-6 py-3 sm:py-2.5 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 touch-manipulation min-h-[48px] sm:min-h-0"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </>
              ) : documentacao ? (
                "Salvar Alterações"
              ) : (
                "Criar Documentação"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DocumentacaoEditModal;
